import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { trainingService } from '../../services/trainingService';
import { formatDateDisplay } from '../../utils/dateUtils';

const statusLabel = (status, requiresSupervisor) => {
  if (status === 'pending') return 'Pending';
  if (status === 'employee_signed') return requiresSupervisor ? 'Awaiting Supervisor' : 'Complete';
  if (status === 'supervisor_signed') return 'Complete';
  return status;
};

const statusColor = (status) => {
  if (status === 'pending') return 'warning';
  if (status === 'employee_signed') return 'info';
  return 'success';
};

const EmployeeTraining = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHrMyTraining = location.pathname === '/hr/my-training';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await trainingService.listMine();
        setItems(res.data || []);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load training');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownloadCompleted = async (row) => {
    setError('');
    try {
      const blob = await trainingService.downloadCompletedPdf(row.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to download completed document');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1.5 }}>
        {isHrMyTraining ? 'My Training' : 'Training Documents'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Signed</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No training documents assigned.</TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.effective_date ? formatDateDisplay(row.effective_date) : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(row.status, row.requires_supervisor_signoff)}
                        color={statusColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {row.employee_signed_at ? formatDateDisplay(row.employee_signed_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {row.status === 'supervisor_signed' && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5 }}
                          onClick={() => handleDownloadCompleted(row)}
                        >
                          Download
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={row.status === 'pending' ? 'contained' : 'outlined'}
                        onClick={() => navigate(`/employee/training/${row.id}`)}
                      >
                        {row.status === 'pending' ? 'Complete' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EmployeeTraining;
